/* ng-resin-print — resin (SLA) 3D-printer hero animation for Null Golf.
   Loads the quantized NGM1 ferrule mesh (baked from Voronoi_LQ.stl) and
   "prints" it layer by layer with a UV-cure glow plane, then loops.

   Asset URLs are passed as attributes so Shopify's fingerprinted asset
   pipeline can supply them:
     <ng-resin-print
       three-src="…/three.module.min.js"
       mesh-src="…/voronoi-ferrule.bin"
       fallback-src="…/hero-photo.jpeg"
       fallback-alt="…"></ng-resin-print> */
(function () {
  if (customElements.get('ng-resin-print')) return;

  const GREEN = 0x1e7a44, GREEN_GLOW = 0x2fd07a;
  const LAYERS = 192, LAYER_MS = 72, DONE_MS = 5200, RESET_MS = 1300;
  const H = 24; // model height in model units

  class NgResinPrint extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      const s = this.style;
      s.display = 'block';
      if (!s.width) s.width = '100%';
      if (!s.height) s.height = '100%';
      if (getComputedStyle(this).position === 'static') s.position = 'relative';
      s.overflow = 'hidden';
      this._buildDom();
      this._reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this._running = false;
      this._boot().catch((e) => { console.warn('ng-resin-print fallback:', e); this._fallback(); });
    }

    disconnectedCallback() {
      this._dead = true;
      if (this._raf) cancelAnimationFrame(this._raf);
      if (this._io) this._io.disconnect();
      if (this._ro) this._ro.disconnect();
      if (this._renderer) this._renderer.dispose();
    }

    // ── DOM: canvas + HUD ────────────────────────────────────────
    _buildDom() {
      const mono = "'JetBrains Mono', ui-monospace, monospace";
      this.innerHTML = '';

      this._canvas = document.createElement('canvas');
      this._canvas.style.cssText = 'position:absolute; inset:0; width:100%; height:100%; display:block; opacity:0; transition:opacity 700ms linear;';
      this.appendChild(this._canvas);

      // loading tag
      this._loadTag = document.createElement('p');
      this._loadTag.textContent = '∅ LOADING GEOMETRY …';
      this._loadTag.style.cssText = 'position:absolute; right:2%; bottom:5%; margin:0; color:rgb(26 26 26 / 0.40); font-family:' + mono + '; font-size:0.5625rem; letter-spacing:0.18em; text-transform:uppercase;';
      this.appendChild(this._loadTag);

      // top-right machine label
      const label = document.createElement('p');
      label.textContent = this.getAttribute('label') || 'MSLA · 405 nm · VORONOI FERRULE / REV A';
      label.style.cssText = 'position:absolute; top:3%; right:2%; margin:0; padding:0.3rem 0.6rem; color:rgb(26 26 26 / 0.45); background:rgb(242 242 240 / 0.85); border:1px solid rgb(26 26 26 / 0.08); font-family:' + mono + '; font-size:0.5625rem; letter-spacing:0.14em; text-transform:uppercase;';
      this.appendChild(label);

      // bottom-right print HUD
      const hud = document.createElement('div');
      hud.style.cssText = 'position:absolute; right:2%; bottom:5%; display:flex; flex-direction:column; gap:0.45rem; align-items:flex-end; min-width:12.5rem; padding:0.7rem 0.8rem; background:rgb(242 242 240 / 0.88); border:1px solid rgb(26 26 26 / 0.10); font-family:' + mono + '; text-transform:uppercase; opacity:0; transition:opacity 400ms linear;';
      hud.innerHTML =
        '<div style="display:flex; justify-content:space-between; gap:1.25rem; width:100%;">' +
          '<span data-ng-status style="display:inline-flex; align-items:center; gap:0.45rem; color:#1A1A1A; font-size:0.5625rem; font-weight:600; letter-spacing:0.14em;">' +
            '<span data-ng-dot style="width:6px; height:6px; background:#1E7A44; border-radius:50%;"></span>CURING LAYER</span>' +
          '<span data-ng-pct style="color:rgb(26 26 26 / 0.45); font-size:0.5625rem; letter-spacing:0.14em;">0%</span>' +
        '</div>' +
        '<div style="display:flex; justify-content:space-between; gap:1.25rem; width:100%; color:rgb(26 26 26 / 0.55); font-size:0.5625rem; letter-spacing:0.14em;">' +
          '<span>LAYER</span><span data-ng-layer style="color:#1A1A1A; font-weight:600;">0000 / 0192</span>' +
        '</div>' +
        '<div style="width:100%; height:3px; background:rgb(26 26 26 / 0.10);">' +
          '<div data-ng-bar style="width:0%; height:100%; background:#1E7A44;"></div>' +
        '</div>' +
        '<div style="display:flex; justify-content:space-between; gap:1.25rem; width:100%; color:rgb(26 26 26 / 0.40); font-size:0.5rem; letter-spacing:0.14em;">' +
          '<span>25 µm SLICE</span><span>MATTE RESIN · BLK</span>' +
        '</div>';
      this.appendChild(hud);
      this._hud = hud;
      this._elStatus = hud.querySelector('[data-ng-status]');
      this._elDot = hud.querySelector('[data-ng-dot]');
      this._elPct = hud.querySelector('[data-ng-pct]');
      this._elLayer = hud.querySelector('[data-ng-layer]');
      this._elBar = hud.querySelector('[data-ng-bar]');
    }

    _fallback() {
      if (this._loadTag) this._loadTag.textContent = '∅ VORONOI FERRULE';
      this.setAttribute('data-ng-ready', '');
      const src = this.getAttribute('fallback-src');
      if (!src) return;
      const img = document.createElement('img');
      img.src = src;
      img.alt = this.getAttribute('fallback-alt') || 'Polished Null Golf club ferrules';
      img.style.cssText = 'position:absolute; inset:0; width:100%; height:100%; object-fit:cover;';
      this.insertBefore(img, this.firstChild);
    }

    // ── three.js scene ───────────────────────────────────────────
    async _boot() {
      const threeSrc = this.getAttribute('three-src');
      const meshSrc = this.getAttribute('mesh-src');
      if (!threeSrc || !meshSrc) throw new Error('ng-resin-print: three-src and mesh-src attributes are required');
      const [THREE, meshBuf] = await Promise.all([
        import(threeSrc),
        fetch(meshSrc).then((r) => { if (!r.ok) throw new Error('mesh ' + r.status); return r.arrayBuffer(); }),
      ]);
      if (this._dead) return;
      this._THREE = THREE;

      // decode NGM1
      const dv = new DataView(meshBuf);
      const nv = dv.getUint32(4, true), nt = dv.getUint32(8, true);
      const pos = new Float32Array(meshBuf, 16, nv * 3);
      const idx = new Uint32Array(meshBuf, 16 + nv * 12, nt * 3);
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      geo.setIndex(new THREE.BufferAttribute(idx, 1));
      geo.rotateX(-Math.PI / 2); // z-up -> y-up, base at y=0
      geo.computeVertexNormals();

      const renderer = new THREE.WebGLRenderer({ canvas: this._canvas, antialias: true, alpha: true });
      renderer.localClippingEnabled = true;
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      this._renderer = renderer;

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(30, 1, 0.1, 300);
      camera.position.set(0, 17, 60);
      this._camera = camera;
      this._scene = scene;

      // lights
      scene.add(new THREE.HemisphereLight(0xf2f2f0, 0x8a8a86, 1.1));
      const key = new THREE.DirectionalLight(0xffffff, 1.6);
      key.position.set(22, 38, 20);
      key.castShadow = true;
      key.shadow.mapSize.set(1024, 1024);
      key.shadow.camera.left = -20; key.shadow.camera.right = 20;
      key.shadow.camera.top = 30; key.shadow.camera.bottom = -20;
      key.shadow.camera.far = 120;
      scene.add(key);
      const rim = new THREE.DirectionalLight(0xf2f2f0, 0.5);
      rim.position.set(-26, 14, -18);
      scene.add(rim);
      this._cure = new THREE.PointLight(GREEN_GLOW, 0, 30, 1.6);
      scene.add(this._cure);

      // group: plate + model (x-shifted right on wide screens)
      const group = new THREE.Group();
      scene.add(group);
      this._group = group;
      this._keyLight = key;

      const shadowPlane = new THREE.Mesh(
        new THREE.PlaneGeometry(80, 80),
        new THREE.ShadowMaterial({ opacity: 0.12 })
      );
      shadowPlane.rotation.x = -Math.PI / 2;
      shadowPlane.position.y = -0.02;
      shadowPlane.receiveShadow = true;
      group.add(shadowPlane);

      // clipping planes (world space; horizontal, so turntable-safe)
      this._clipTop = new THREE.Plane(new THREE.Vector3(0, -1, 0), 0);      // keep y <= h
      this._clipBottom = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);    // keep y >= h - t (slice only)

      const spin = new THREE.Group(); // turntable
      group.add(spin);
      this._spin = spin;

      const bodyMat = new THREE.MeshPhysicalMaterial({
        color: 0x1f1f1f, roughness: 0.34, metalness: 0.06,
        clearcoat: 0.55, clearcoatRoughness: 0.45,
        side: THREE.DoubleSide, clippingPlanes: [this._clipTop], clipShadows: true,
      });
      const body = new THREE.Mesh(geo, bodyMat);
      body.castShadow = true;
      spin.add(body);

      const sliceMat = new THREE.MeshBasicMaterial({
        color: GREEN, side: THREE.DoubleSide, toneMapped: false, transparent: true, opacity: 0.85,
        clippingPlanes: [this._clipTop, this._clipBottom],
      });
      this._slice = new THREE.Mesh(geo, sliceMat);
      this._sliceMat = sliceMat;
      spin.add(this._slice);

      this._lookAt = new THREE.Vector3(0, 10.5, 0);

      // sizing
      this._ro = new ResizeObserver(() => this._resize());
      this._ro.observe(this);
      this._resize();

      // visibility gating
      this._visible = true;
      this._io = new IntersectionObserver((en) => {
        this._visible = en[0].isIntersecting;
        if (this._visible) this._kick();
      }, { threshold: 0.05 });
      this._io.observe(this);
      document.addEventListener('visibilitychange', () => { if (!document.hidden) this._kick(); });

      // state
      this._phase = 'print';
      this._t = 0;
      this._layer = 0;
      this._flash = 0;
      this._spinV = 0.22;
      this._last = performance.now();

      // respond to reduced-motion flips at runtime (mirrors making-motion.js):
      // reduce -> freeze on the finished print; un-reduce -> resume the loop
      const reducedQuery = matchMedia('(prefers-reduced-motion: reduce)');
      this._reduced = reducedQuery.matches; // re-read: may have flipped during boot
      const onReducedChange = (e) => {
        if (this._dead) return;
        this._reduced = e.matches;
        if (this._reduced) {
          if (this._raf) { cancelAnimationFrame(this._raf); this._raf = null; }
          this._setHeight(H);
          this._slice.visible = false;
          this._cure.intensity = 0;
          this._spin.rotation.y = 0;
          this._hudDone();
          renderer.render(scene, camera);
        } else {
          this._kick();
        }
      };
      if (reducedQuery.addEventListener) {
        reducedQuery.addEventListener('change', onReducedChange);
      } else if (reducedQuery.addListener) {
        reducedQuery.addListener(onReducedChange);
      }

      if (this._reduced) {
        this._setHeight(H);
        this._slice.visible = false;
        this._hudDone();
        this._hud.style.opacity = '1';
        this._loadTag.remove();
        this._canvas.style.opacity = '1';
        renderer.render(scene, camera);
        this.setAttribute('data-ng-ready', '');
        return;
      }

      this._loadTag.remove();
      this._canvas.style.opacity = '1';
      this._hud.style.opacity = '1';
      this.setAttribute('data-ng-ready', '');
      this._kick();
    }

    _resize() {
      if (!this._renderer) return;
      const w = this.clientWidth || 1, h = this.clientHeight || 1;
      const dpr = Math.min(2, devicePixelRatio || 1);
      this._renderer.setSize(w, h, false);
      this._renderer.setPixelRatio(dpr);
      const aspect = w / h;
      this._camera.aspect = aspect;
      // frame the model: on the wide desktop hero the copy sits left, so shift
      // the model right; in the mobile banner (viewport < 750px) there is no
      // copy overlay, so shift it left, clear of the bottom-right HUD
      const dist = aspect < 1 ? 74 : 60;
      this._camera.position.set(0, 17, dist);
      const mobile = (window.innerWidth || w) < 750;
      const half = Math.tan((this._camera.fov * Math.PI) / 360) * dist * aspect;
      let shift = 0;
      if (mobile) shift = aspect > 1.15 ? -half * 0.4 : 0;
      else if (aspect > 1.15) shift = half * 0.42;
      this._group.position.x = shift;
      this._lookAt.set(0, 10.5, 0);
      this._camera.lookAt(this._lookAt);
      this._camera.updateProjectionMatrix();
      if (this._reduced && this._scene) this._renderer.render(this._scene, this._camera);
    }

    _setHeight(h) {
      const t = (H / LAYERS) * 1.6;
      this._clipTop.constant = h;
      this._clipBottom.constant = -(h - t);
      this._cure.position.set(this._group.position.x, h + 0.6, 3);
    }

    _kick() {
      if (this._raf || this._dead || this._reduced || !this._renderer) return;
      this._last = performance.now();
      this._raf = requestAnimationFrame((n) => this._tick(n));
    }

    // ── loop: print -> done -> reset ─────────────────────────────
    _tick(now) {
      this._raf = null;
      if (this._dead) return;
      if (!this._visible || document.hidden) return; // resumes via _kick
      const dt = Math.min(100, now - this._last);
      this._last = now;
      this._t += dt;

      this._spin.rotation.y += this._spinV * (dt / 1000);
      this._flash *= Math.pow(0.86, dt / 16.7);

      if (this._phase === 'print') {
        const layer = Math.min(LAYERS, Math.floor(this._t / LAYER_MS));
        if (layer !== this._layer) {
          this._layer = layer;
          this._flash = 1;
          this._hudPrint(layer);
        }
        this._setHeight((layer / LAYERS) * H + 0.001);
        this._slice.visible = true;
        this._cure.intensity = 10 + 34 * this._flash;
        if (layer >= LAYERS) { this._phase = 'done'; this._t = 0; this._hudDone(); }
      } else if (this._phase === 'done') {
        this._setHeight(H + 1);
        this._slice.visible = false;
        this._cure.intensity *= Math.pow(0.9, dt / 16.7);
        // brief victory spin that eases back down
        const p = Math.min(1, this._t / 1600);
        this._spinV = 0.22 + Math.sin(p * Math.PI) * 1.35;
        if (this._t >= DONE_MS) { this._phase = 'reset'; this._t = 0; this._hudReset(); }
      } else { // reset: fast wipe down
        const p = Math.min(1, this._t / RESET_MS);
        const e = p * p * (3 - 2 * p);
        this._setHeight((1 - e) * H + 0.001);
        this._slice.visible = true;
        this._cure.intensity = 8;
        this._spinV = 0.22;
        if (p >= 1) {
          this._phase = 'print'; this._t = 0; this._layer = -1; this._flash = 0;
        }
      }

      this._renderer.render(this._scene, this._camera);
      this._raf = requestAnimationFrame((n) => this._tick(n));
    }

    // ── HUD states ───────────────────────────────────────────────
    _hudPrint(layer) {
      const pct = Math.round((layer / LAYERS) * 100);
      this._elLayer.textContent = String(layer).padStart(4, '0') + ' / 0' + LAYERS;
      this._elPct.textContent = pct + '%';
      this._elBar.style.width = pct + '%';
      if (this._hudMode !== 'print') {
        this._hudMode = 'print';
        this._elStatus.childNodes[1].textContent = 'CURING LAYER';
        this._elStatus.style.color = '#1A1A1A';
        this._elDot.style.background = '#1E7A44';
        this._elDot.style.animation = 'ngCureDot 900ms steps(2, jump-none) infinite';
      }
    }
    _hudDone() {
      this._hudMode = 'done';
      this._elStatus.childNodes[1].textContent = 'PRINT COMPLETE';
      this._elStatus.style.color = '#1E7A44';
      this._elDot.style.animation = 'none';
      this._elDot.style.background = '#1E7A44';
      this._elLayer.textContent = '0' + LAYERS + ' / 0' + LAYERS;
      this._elPct.textContent = '100%';
      this._elBar.style.width = '100%';
    }
    _hudReset() {
      this._hudMode = 'reset';
      this._elStatus.childNodes[1].textContent = 'RESIN BATH / NEXT RUN';
      this._elStatus.style.color = '#1A1A1A';
      this._elDot.style.background = 'rgb(26 26 26 / 0.35)';
      this._elDot.style.animation = 'none';
      this._elLayer.textContent = '0000 / 0' + LAYERS;
      this._elPct.textContent = '0%';
      this._elBar.style.width = '0%';
    }
  }

  // ensure dot keyframes exist up front
  if (!document.getElementById('ng-cure-dot-kf')) {
    const st = document.createElement('style');
    st.id = 'ng-cure-dot-kf';
    st.textContent = '@keyframes ngCureDot { 0% { opacity: 1; } 100% { opacity: 0.25; } }';
    document.head.appendChild(st);
  }

  customElements.define('ng-resin-print', NgResinPrint);
})();
